import React, { FocusEvent, useMemo } from 'react';
import Modal from 'react-modal';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import dayjs from 'dayjs';
import advancedFormatPlugin from 'dayjs/plugin/advancedFormat';
import timezonePlugin from 'dayjs/plugin/timezone';
import { useForm, Controller, useFormState } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { isNil, pathOr, is } from 'ramda';

import 'react-datepicker/dist/react-datepicker.css';

import { Button, ErrorMessage, formMessages, Select, SelectArrow, SelectOption } from '@components/form';
import { useYupValidationResolver } from '@hooks/useYup';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useAddShoppingCartItem } from '@hooks/useShoppingCart';
import reportError from '@utils/reportError';
import { Variant } from '@definitions/giftCertificate.types';
import { GiftCertificate } from '@definitions/shoppingCart.types';
import { useHeaderSublevel } from '@hooks/useUi';
import { SUBLEVEL } from '@components/header/header.constants';
import { renderWhen } from '@utils/rendering';

import {
  ModalContent,
  Title,
  modalStyles,
  CloseModal,
  ModalGlobalStyles,
  Form,
  Subtitle,
  Row,
  TextField,
  SelectWrapper,
  DateWrapper,
} from './giftCertificateModal.styles';

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(timezonePlugin);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(advancedFormatPlugin);

const MIN_AMOUNT = 100;
const MAX_AMOUNT = 10000;
const PREDEFINED_AMOUNTS = [100, 200, 500, 1000, 1500, 2000];
const PREDEFINED_AMOUNT = 'predefined_amount';
const AMOUNT = 'amount';
const RECIPIENT_EMAIL = 'recipient_email';
const RECIPIENT_NAME = 'recipient_name';
const SENDER_EMAIL = 'sender_email';
const SENDER_NAME = 'sender_name';
const GIFT_MESSAGE = 'gift_message';
const SCHEDULE_DATE = 'schedule_date';
interface FormProps {
  [PREDEFINED_AMOUNT]: string;
  [AMOUNT]: number | string;
  [RECIPIENT_EMAIL]: string;
  [RECIPIENT_NAME]: string;
  [SENDER_EMAIL]: string;
  [SENDER_NAME]: string;
  [GIFT_MESSAGE]: string;
  [SCHEDULE_DATE]: Date;
}

export interface GiftCertificateModalProps {
  isOpen: boolean;
  theme?: Variant;
  setOpen: (isOpen: boolean) => void;
}

export const GiftCertificateModal = ({ isOpen, setOpen, theme }: GiftCertificateModalProps) => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();
  const { openHeaderSublevel } = useHeaderSublevel();
  const { addCartItem } = useAddShoppingCartItem();
  const betweenValidationMessage = intl.formatMessage({
    id: 'giftCertificateModal.betweenValidationMessage',
    defaultMessage: 'The amount needs to be between $100 and $10000',
  });
  const numberValidationMessage = intl.formatMessage({
    id: 'giftCertificateModal.numberValidationMessage',
    defaultMessage: 'The amount needs to be an integer',
  });
  const amountMessage = intl.formatMessage({
    id: 'giftCertificateModal.amount',
    defaultMessage: 'Select Amount',
  });
  const validationSchema = useMemo(
    () =>
      yup.object({
        [AMOUNT]: yup
          .number()
          .transform((cv: number) => (isNaN(cv) ? undefined : cv))
          .integer(numberValidationMessage)
          .min(MIN_AMOUNT, betweenValidationMessage)
          .max(MAX_AMOUNT, betweenValidationMessage),
        [RECIPIENT_EMAIL]: yup
          .string()
          .email(intl.formatMessage(formMessages.emailError))
          .required(intl.formatMessage(formMessages.required)),
        [RECIPIENT_NAME]: yup.string().required(intl.formatMessage(formMessages.required)),
        [SENDER_EMAIL]: yup
          .string()
          .email(intl.formatMessage(formMessages.emailError))
          .required(intl.formatMessage(formMessages.required)),
        [SENDER_NAME]: yup.string().required(intl.formatMessage(formMessages.required)),
        [GIFT_MESSAGE]: yup.string(),
        [SCHEDULE_DATE]: yup.string(),
        [PREDEFINED_AMOUNT]: yup
          .string()
          .when(AMOUNT, { is: isNil, then: yup.string().required(intl.formatMessage(formMessages.required)) }),
      }),
    []
  );

  const resolver = useYupValidationResolver(validationSchema);
  const { control, register, handleSubmit, setValue, clearErrors } = useForm<FormProps>({
    defaultValues: {
      [AMOUNT]: '',
      [PREDEFINED_AMOUNT]: '',
    },
    resolver,
  });
  const { errors } = useFormState({ control });

  const handleAddToBag = async (data: FormProps) => {
    try {
      const message = {
        message: data[GIFT_MESSAGE],
        date: data[SCHEDULE_DATE] ? dayjs(data[SCHEDULE_DATE]).format() : null,
      };
      const giftCertificate = {
        amount: data[PREDEFINED_AMOUNT] === '' ? data[AMOUNT] : data[PREDEFINED_AMOUNT],
        name: theme?.value.name,
        theme: theme?.id,
        message: JSON.stringify(message),
        quantity: 1,
        sender: {
          name: data[SENDER_NAME],
          email: data[SENDER_EMAIL],
        },
        recipient: {
          name: data[RECIPIENT_NAME],
          email: data[RECIPIENT_EMAIL],
        },
      } as GiftCertificate;

      await addCartItem({ giftCertificate });
      setOpen(false);
      openHeaderSublevel({ sublevel: SUBLEVEL.BAG });
    } catch (e) {
      reportError(e);
    }
  };

  const handleAmountBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = pathOr(null, ['target', 'value'], e);

    if (value) {
      setValue(PREDEFINED_AMOUNT, '');
    }
  };

  const handlePredefinedAmountChange = () => {
    setValue(AMOUNT, '');
    clearErrors(AMOUNT);
  };
  const renderError = renderWhen(is(Object), (error) => <ErrorMessage>{error?.message}</ErrorMessage>);

  return (
    <Modal isOpen={isOpen} style={modalStyles} onRequestClose={() => setOpen(false)} closeTimeoutMS={200}>
      <ModalGlobalStyles />
      <ModalContent>
        <CloseModal onClick={() => setOpen(false)} />
        <Title>
          <FormattedMessage id="giftCertificateModal.giftCard" defaultMessage="Gift Card:" />
        </Title>
        <Form onSubmit={handleSubmit(handleAddToBag)}>
          <Subtitle>
            <FormattedMessage id="giftCertificateModal.value" defaultMessage="Card Value:" />
          </Subtitle>
          <Row columns={3}>
            <SelectWrapper>
              <Select id={PREDEFINED_AMOUNT} {...register(PREDEFINED_AMOUNT)} onChange={handlePredefinedAmountChange}>
                <SelectOption value="" disabled>
                  {amountMessage}
                </SelectOption>
                {PREDEFINED_AMOUNTS.map((amount, index) => (
                  <SelectOption value={amount} key={index}>
                    ${amount}
                  </SelectOption>
                ))}
              </Select>
              <SelectArrow />
              {renderError(errors[PREDEFINED_AMOUNT])}
            </SelectWrapper>
            <span>
              <FormattedMessage id="giftCertificateModal.or" defaultMessage="or" />
            </span>
            <TextField
              register={register(AMOUNT)}
              error={errors[AMOUNT]}
              type="number"
              pattern="[0-9]*"
              step="any"
              onBlur={handleAmountBlur}
              placeholder={intl.formatMessage({
                id: 'giftCertificateModal.input',
                defaultMessage: 'Custom amount ($):',
              })}
            />
          </Row>
          <Subtitle>
            <FormattedMessage id="giftCertificateModal.recipient" defaultMessage="Recipient:" />
          </Subtitle>
          <Row>
            <TextField
              register={register(RECIPIENT_EMAIL)}
              error={errors[RECIPIENT_EMAIL]}
              placeholder={intl.formatMessage(formMessages.email)}
            />
            <TextField
              register={register(RECIPIENT_NAME)}
              error={errors[RECIPIENT_NAME]}
              placeholder={intl.formatMessage(formMessages.name)}
            />
          </Row>
          <Subtitle>
            <FormattedMessage id="giftCertificateModal.sender" defaultMessage="Sender:" />
          </Subtitle>
          <Row>
            <TextField
              register={register(SENDER_EMAIL)}
              error={errors[SENDER_EMAIL]}
              placeholder={intl.formatMessage(formMessages.email)}
            />
            <TextField
              register={register(SENDER_NAME)}
              error={errors[SENDER_NAME]}
              placeholder={intl.formatMessage(formMessages.name)}
            />
            <TextField
              register={register(GIFT_MESSAGE)}
              error={errors[GIFT_MESSAGE]}
              textarea
              optional
              placeholder={intl.formatMessage({
                id: 'giftCertificateModal.giftMessage',
                defaultMessage: 'Gift Message:',
              })}
            />
          </Row>
          <Subtitle>
            <FormattedMessage id="giftCertificateModal.schedule" defaultMessage="Schedule Gift Card Email:" />
          </Subtitle>
          <Row>
            <DateWrapper>
              <Controller
                control={control}
                name={SCHEDULE_DATE}
                render={({ field: { onChange, value } }) => {
                  const isCurrentDay = dayjs().get('date') === dayjs(value).get('date');
                  const timeRange =
                    isCurrentDay || !value
                      ? {
                          minTime: dayjs().add(1, 'hour').toDate(),
                          maxTime: dayjs().endOf('day').toDate(),
                        }
                      : {};

                  return (
                    <DatePicker
                      onChange={onChange}
                      selected={value}
                      minDate={new Date()}
                      dateFormat="MM/dd/yyyy h:mm aa"
                      showTimeInput={isMobile}
                      showTimeSelect={!isMobile}
                      popperPlacement="top"
                      placeholderText={`MM/DD/YYYY HH:MM ${dayjs().format('z')}`}
                      {...timeRange}
                    />
                  );
                }}
              />
              <SelectArrow />
            </DateWrapper>
          </Row>
          <Button fullWidth inverted>
            <FormattedMessage id="giftCertificateModal.add" defaultMessage="ADD TO BAG" />
          </Button>
        </Form>
      </ModalContent>
    </Modal>
  );
};
